<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocaleStore } from '@/stores/locale'
import { LOCALE_OPTIONS } from '@/i18n/locales'
import type { SupportedLocale } from '@/types/i18n'

const { t } = useI18n()
const localeStore = useLocaleStore()

const isOpen = ref<boolean>(false)
const rootRef = ref<HTMLElement | null>(null)

const currentLabel = computed<string>(() => {
  const active = LOCALE_OPTIONS.find((option) => option.code === localeStore.locale)
  return active?.label ?? ''
})

const toggleOpen = (): void => {
  isOpen.value = !isOpen.value
}

const select = (code: SupportedLocale): void => {
  localeStore.setLocale(code)
  isOpen.value = false
}

const handleDocumentClick = (event: MouseEvent): void => {
  if (!isOpen.value) return

  const rootEl = rootRef.value
  if (!rootEl) return

  const target = event.target as Node | null
  if (target && !rootEl.contains(target)) isOpen.value = false
}

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') isOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <div ref="rootRef" class="language-selector">
    <button
      class="selector-trigger"
      type="button"
      :aria-label="t('topbar.selectLanguage')"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      @click.stop="toggleOpen"
    >
      <span>{{ currentLabel }}</span>
      <span class="chevron">{{ isOpen ? '▴' : '▾' }}</span>
    </button>

    <ul v-if="isOpen" class="selector-menu" role="listbox">
      <li
        v-for="option in LOCALE_OPTIONS"
        :key="option.code"
        role="option"
        :aria-selected="option.code === localeStore.locale"
      >
        <button
          class="selector-option"
          :class="{ active: option.code === localeStore.locale }"
          type="button"
          :lang="option.code"
          @click="select(option.code)"
        >
          {{ option.label }}
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.language-selector {
  position: relative;
}

.selector-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f8fafc;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #475569;
  cursor: pointer;
}

.selector-trigger:hover {
  background: #f1f5f9;
}

.chevron {
  font-size: 0.625rem;
  color: #94a3b8;
}

.selector-menu {
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  min-width: 100%;
  margin: 0;
  padding: 0.25rem;
  list-style: none;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  z-index: 60;
}

.selector-option {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: none;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #475569;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

.selector-option:hover {
  background: #f1f5f9;
}

.selector-option.active {
  color: #1e293b;
  font-weight: 600;
}
</style>
